/**
 * Maps Shipeezi application routes to Dumont AI agent types
 * This ensures the correct agent is selected based on the current section
 */

export type AgentSection = 'business' | 'operations' | 'warehousing';

export type AgentTypeFromSection = 'sales' | 'operations' | 'warehouse';

/**
 * Routes that should show the default Dumont message (no specific agent)
 * These sections are general/cross-functional
 */
const GENERAL_ROUTES: string[] = [
  '/app/dashboard',
  '/app/reports',
  '/app/network',
  '/app/settings',
  '/app/help',
];

/**
 * Route prefix to section mapping
 * More specific routes should come first
 */
const ROUTE_TO_SECTION: Record<string, AgentSection> = {
  // Business section routes
  '/app/leads': 'business',
  '/app/quotes': 'business',
  '/app/customers': 'business',
  '/app/sales': 'business',
  '/app/business': 'business',
  '/app/contacts': 'business',
  '/app/accounts': 'business',
  '/app/opportunities': 'business',
  
  // Operations section routes
  '/app/orders': 'operations',
  '/app/jobs': 'operations',
  '/app/dispatch': 'operations',
  '/app/dispatch-operation': 'operations',
  '/app/operations': 'operations',
  '/app/shipments': 'operations',
  '/app/deliveries': 'operations',
  '/app/tracking': 'operations',
  '/app/routes': 'operations',
  '/app/schedule': 'operations',
  
  // Warehousing section routes
  '/app/warehousing': 'warehousing',
  '/app/warehouse': 'warehousing',
  '/app/inventory': 'warehousing',
  '/app/storage': 'warehousing',
  '/app/storage-box-unit': 'warehousing',
  '/app/movements': 'warehousing',
  '/app/transshipment': 'warehousing',
  '/app/storage-account': 'warehousing',
};

/**
 * Section to agent type mapping
 */
const SECTION_TO_AGENT_TYPE: Record<AgentSection, AgentTypeFromSection> = {
  'business': 'sales',
  'operations': 'operations',
  'warehousing': 'warehouse',
};

/**
 * Agent welcome messages by type
 */
export const AGENT_WELCOME_MESSAGES: Record<AgentTypeFromSection | 'default', (userName: string) => string> = {
  'sales': (userName) => 
    `Hi, ${userName},\nI'm Dumont Business Assistant! I specialize in sales and quotes. I can help you create accurate freight quotes, manage customer relationships, and close deals faster. How can I assist you today?`,
  'operations': (userName) => 
    `Hi, ${userName},\nI'm Dumont Operations Assistant! I'm your operations expert. I can help you track shipments, monitor deliveries, manage jobs, and ensure smooth logistics operations. What do you need help with?`,
  'warehouse': (userName) => 
    `Hi, ${userName},\nI'm Dumont Warehousing Assistant! I specialize in inventory and warehouse operations. I can help you manage stock levels, track inventory, coordinate storage, and optimize warehouse efficiency. How can I help?`,
  'default': (userName) => 
    `Hi, ${userName},\nI'm Dumont, your AI assistant specialized in logistics and moving. Built to simplify complex workflows, I continuously learn to deliver faster, smarter, and more accurate support for your business.`,
};

/**
 * Agent names by type
 */
export const AGENT_NAMES: Record<AgentTypeFromSection | 'default', string> = {
  'sales': 'Dumont Business Assistant',
  'operations': 'Dumont Operations Assistant',
  'warehouse': 'Dumont Warehousing Assistant',
  'default': 'Dumont',
};

/**
 * Checks if the current route is a general route (Dashboard, Reports, Network, etc.)
 * These routes should show the default Dumont message without a specific agent
 * @param pathname - The current URL pathname
 * @returns true if it's a general route
 */
export function isGeneralRoute(pathname: string): boolean {
  const normalizedPath = pathname.toLowerCase();
  
  return GENERAL_ROUTES.some(route => normalizedPath.startsWith(route.toLowerCase()));
}

/**
 * Detects the current section from the URL pathname
 * @param pathname - The current URL pathname
 * @returns The detected section or null if not found
 */
export function detectSectionFromRoute(pathname: string): AgentSection | null {
  const normalizedPath = pathname.toLowerCase();
  
  // General routes (Dashboard, Reports, Network) should not have a specific agent
  if (isGeneralRoute(normalizedPath)) {
    return null;
  }
  
  // Check exact matches first, then prefix matches
  for (const [route, section] of Object.entries(ROUTE_TO_SECTION)) {
    if (normalizedPath.startsWith(route.toLowerCase())) {
      return section;
    }
  }
  
  return null;
}

/**
 * Gets the agent type for a given route
 * @param pathname - The current URL pathname
 * @returns The agent type or null if not detected (general routes return null)
 */
export function getAgentTypeFromRoute(pathname: string): AgentTypeFromSection | null {
  // General routes should always return null to show default Dumont message
  if (isGeneralRoute(pathname)) {
    return null;
  }
  
  const section = detectSectionFromRoute(pathname);
  
  if (section) {
    return SECTION_TO_AGENT_TYPE[section];
  }
  
  return null;
}

/**
 * Gets the welcome message for a given agent type
 * @param agentType - The agent type
 * @param userName - The user's name
 * @returns The welcome message
 */
export function getWelcomeMessage(agentType: AgentTypeFromSection | null, userName: string): string {
  if (agentType && AGENT_WELCOME_MESSAGES[agentType]) {
    return AGENT_WELCOME_MESSAGES[agentType](userName);
  }
  return AGENT_WELCOME_MESSAGES['default'](userName);
}

/**
 * Gets the agent name for a given agent type
 * @param agentType - The agent type
 * @returns The agent name
 */
export function getAgentName(agentType: AgentTypeFromSection | null): string {
  if (agentType && AGENT_NAMES[agentType]) {
    return AGENT_NAMES[agentType];
  }
  return AGENT_NAMES['default'];
}
